import React, { FC, useCallback, useEffect, useState } from 'react';

import { PREFERENCE__COMMENTS__LIMIT } from '../../../constants/pagination';
import useIsMobile from '../../../hooks/useIsMobile';
import translate from '../../../i18n/translate';

import { Comment } from '../../../models/comment';
import CommentSingle from '../CommentSingle';

import * as Styled from '../styled';

export type Props = {
  comments: Comment[];
  isNewComment: boolean;
};

/**
 * A list of comments for a message
 *
 * @param props              Props passed to the component
 * @param props.comments     The comments to show
 * @param props.isNewComment Whether new comment was created by the user
 * @returns                  The component itself
 */
const CommentList: FC<Props> = ({ comments, isNewComment }) => {
  const isMobile = useIsMobile();
  const [currentCursor, setCurrentCursor] = useState<string>(
    comments[comments.length - 1]?.id ?? '',
  );

  useEffect(() => {
    if (isNewComment) {
      setCurrentCursor(comments[comments.length - 1]?.id ?? '');
      return;
    }

    const newCursor = comments[PREFERENCE__COMMENTS__LIMIT - 1]?.id;
    if (currentCursor !== newCursor) {
      setCurrentCursor(newCursor ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments]);

  const firstCommentRef = useCallback(
    (node: HTMLLIElement) => {
      if (isMobile && node !== null) {
        node.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest',
        });
      }
    },
    [isMobile],
  );

  if (isMobile && comments.length === 0) {
    return (
      <Styled.WrapperNoComments>
        {translate('COMMENTS__EMPTY')}
      </Styled.WrapperNoComments>
    );
  }

  return (
    <ul>
      {comments.map(comment => {
        return (
          <li
            key={comment.id}
            ref={currentCursor === comment.id ? firstCommentRef : undefined}
          >
            <CommentSingle comment={comment} />
          </li>
        );
      })}
    </ul>
  );
};

export default CommentList;
